import { List } from "immutable";
import Moment from "moment";
import { push } from "react-router-redux";
import { createAction } from "redux-actions";
import * as AbstractActions from "./AbstractActions";
import * as AbstractGradingActions from "./AbstractGradingActions";
import * as AdminAbstractActions from "./AdminAbstractActions";
import * as AdminEventActions from "./AdminEventActions";
import * as CareerCenterActions from "./CareerCenterActions";
import { Thunk } from "./Thunk";
import { Paths } from "../constants";
import { DefaultReviewerStats } from "../models/abstract";
import { AuthTokenDecoded, LegacyAuthCookie } from "../models/auth";
import { Result } from "../models/result";
import { Api } from "../services/Api";
import { LegacyApi } from "../services/LegacyApi";
import { Utility, AuthHelper } from "../utils";
import * as authHelper from "../utils/AuthHelper";
import { Cookie } from "../utils/Cookie";
import { Log } from "../utils/Log";

const prefix = "AUTH_";

export class Constants {
    public static CLIENT_CREDS: string = prefix + "CLIENT_CREDS";
    public static INITIALIZED: string = prefix + "INITIALIZED";
    public static ON_AUTH_EXPIRED: string = prefix + "ON_AUTH_EXPIRED";
    public static ON_AUTH_FAILED: string = prefix + "ON_AUTH_FAILED";
    public static ON_AUTH_SUCCESS: string = prefix + "ON_AUTH_COMPLETE";
    public static ON_ACCESS_REVOKED: string = prefix + "ON_ACCESS_REVOKED";
    public static USERGUID_CHANGED = prefix + "USERGUID_CHANGED";
    public static LegacyAuthCookie = "ASRS_LEGACY_AUTH";
}

/** Timeout to keep track of when the current access token expires */
let expireTimeout: NodeJS.Timer | undefined;

/** Timeout to keep track of changes in legacy auth cookie */
let legacyCheckTimeout: NodeJS.Timer | undefined;

let authTryOuts = 0;

/** Used to track changes of legacy auth cookie */
let legacyAuthCookie: LegacyAuthCookie | undefined;

export type AuthFailedPayload = Result<any>;

export interface AuthSuccessPayload {
    accessToken: string;
    refreshToken: string;
}

export interface AuthClientCredsPayload {
    clientId: string;
    clientSecret: string;
}

/**
 * Initiates the auth state
 * @param clientId The client ID
 * @param clientSecret The client secret
 */
export const init = (clientId: string, clientSecret: string): Thunk => async (dispatch, getState) => {

    // set client id/secret into auth state
    dispatch(clientCreds(clientId, clientSecret));

    const isSSR = getState().ssr.isSSR;
    if (isSSR) {
        // server-side rendering. Authcheck is taken care of there.
    }
    else {
        // not connected to api
        if (!getState().conn.connected) {
            return;
        }

        let username: string | undefined;

        // check local storage token object
        const storageToken = authHelper.getTokenFromCookie();

        // check for legacy login
        legacyAuthCookie = Cookie.getAs<LegacyAuthCookie>(Constants.LegacyAuthCookie);
        if (legacyAuthCookie && legacyAuthCookie.s === "legacy" && (legacyAuthCookie.m || legacyAuthCookie.u)) {
            // legacy login detected
            const api = new Api(getState());
            const legacyAuthResult = await api.authorize({
                clientId,
                clientSecret,
                legacyMember: legacyAuthCookie.m,
                legacyUser: legacyAuthCookie.u,
                grantType: "legacy",
            });

            if (legacyAuthResult.isSuccess && legacyAuthResult.data !== undefined) {
                authHelper.saveTokenToCookie(legacyAuthResult.data.access_token, legacyAuthResult.data.refresh_token);
                await dispatch(postAuthorize(legacyAuthResult.data.access_token, legacyAuthResult.data.refresh_token));
                await dispatch(initialized());

                const decodedToken = new AuthTokenDecoded(legacyAuthResult.data.access_token, legacyAuthResult.data.refresh_token);
                if (decodedToken.tokenData) {
                    username = decodedToken.tokenData.username;
                    await dispatch(tryLegacyAuthorize(username, legacyAuthCookie));
                }

                return;
            }
        }

        if (storageToken !== undefined) {
            const decodedToken = new AuthTokenDecoded(storageToken.accessToken, storageToken.refreshToken);

            if (!decodedToken.isValid)
                dispatch(revokeAccess());
            else {
                const isExpired = decodedToken.isExpired();
                const isExpiredRefreshToken = decodedToken.isExpiredRefreshToken();

                if (decodedToken.tokenData)
                    username = decodedToken.tokenData.username;

                Log.info("Expired token?", isExpired);
                Log.info("Expired refresh token?", isExpiredRefreshToken);

                // console.info("Expired token?", isExpired);
                // console.info("Expired refresh token?", isExpiredRefreshToken);

                if (!isExpired) {
                    // dispatch auth success (even though we don't yet know if the access token is valid!)
                    await dispatch(postAuthorize(storageToken.accessToken, storageToken.refreshToken));
                    // We don't know if access is valid, so we need to check auth status

                    const api = new Api(getState());
                    const statusResult = await api.authCheck();
                    const isConnectionFail = Result.isConnectionFail(statusResult);
                    if (!statusResult.isSuccess && !isConnectionFail) {
                        Log.warn("Auth status check failed. Need to revoke access.");
                        await dispatch(revokeUserAccess(clientId, clientSecret));
                    }

                    await dispatch(initialized());
                }
                else if (isExpired && !isExpiredRefreshToken) {
                    // assume expired token was a valid one and run post-auth sequence.
                    // this prevents login page from being temporarily visible.
                    // console.warn("dispatch post auth");
                    // await dispatch(postAuthorize(storageToken.accessToken, storageToken.refreshToken));

                    // dispatch refresh token action
                    await dispatch(refreshAccessToken(clientId, clientSecret, storageToken.refreshToken));
                    await dispatch(initialized());
                }
                else {
                    // tokens expired, revoke access, try to slient authorize
                    await dispatch(revokeUserAccess(clientId, clientSecret));
                    await dispatch(initialized());
                }

                await dispatch(tryLegacyAuthorize(username, legacyAuthCookie));
            }
        }
        else {
            // Not authorized or all tokens expired
            await dispatch(revokeUserAccess(clientId, clientSecret));
            await dispatch(initialized());
        }
    }
};

/** Starts the timer for checking access token expiration */
export const beginExpirationTimeout = (): Thunk => async (dispatch, getState) => {
    const state = getState();

    if (state.auth.data) {
        const now = new Date();
        const expires = Utility.utcToLocal(Moment.unix(state.auth.data.expiresOnUTC).toDate());
        const expiresIn = ((expires || now).getTime() - now.getTime()) / 1000;

        // console.log("beginExpirationTimeout, expiresIn", expiresIn);

        if (expireTimeout !== undefined)
            clearTimeout(expireTimeout);

        if (expiresIn > 0) {
            expireTimeout = setTimeout(async () => {
                await dispatch(beginExpirationTimeout());
            }, 1000);
        }
        else {
            Log.info("beginExpirationTimeout, Auth expired");
            await dispatch(refreshAccessToken(state.auth.clientId, state.auth.clientSecret, state.auth.refreshToken || ""));
        }
    }
    else
        Log.info("beginExpirationTimeout, No auth data");
};

export const beginLegacyAuthCheck = (): Thunk => async (dispatch, getState) => {
    const state = getState();
    const thisAuthCookie = Cookie.getAs<LegacyAuthCookie>(Constants.LegacyAuthCookie);
    const legacyStr = legacyAuthCookie ? JSON.stringify(legacyAuthCookie) : "";
    const currentStr = thisAuthCookie ? JSON.stringify(thisAuthCookie) : "";

    const currMember = legacyAuthCookie ? legacyAuthCookie.m : undefined;
    const currUser = legacyAuthCookie ? legacyAuthCookie.u : undefined;
    const newMember = thisAuthCookie ? thisAuthCookie.m : undefined;
    const newUser = thisAuthCookie ? thisAuthCookie.u : undefined;

    if (legacyCheckTimeout !== undefined)
        clearTimeout(legacyCheckTimeout);

    if (currentStr !== legacyStr && thisAuthCookie) {
        let clearUserRsx = true;
        // don't clear user resources
        if ((!currMember && !!newMember) || (!currUser && !!newUser))
            clearUserRsx = false;

        if (legacyAuthCookie && thisAuthCookie && legacyAuthCookie.m === thisAuthCookie.m && legacyAuthCookie.u === thisAuthCookie.u)
            clearUserRsx = false;

        // clear all user resources
        if (clearUserRsx)
            await dispatch(clearUserResources());

        if (thisAuthCookie.s === "asrs.client") {
            // change came from another app (not legacy)
            console.log("login changed from a client app. legacy auth cookie", legacyAuthCookie, thisAuthCookie);
            await dispatch(init(state.auth.clientId, state.auth.clientSecret));
        }
        else {
            // change came from legacy, so just log out completely
            console.log("login changed from legacy. legacy auth cookie", legacyAuthCookie, thisAuthCookie);
            await dispatch(revokeAccess());
            window.location.reload(true);
        }

        // if (thisAuthCookie.u || thisAuthCookie.m) {
        //     await dispatch(clearUserResources());
        //     await dispatch(init(state.auth.clientId, state.auth.clientSecret));
        // }
        // else
        //     await dispatch(logOut());
    }
    else
        legacyCheckTimeout = setTimeout(async () => {
            await dispatch(beginLegacyAuthCheck());
        }, 5000);
};

/**
 * Authorizes the client against the API
 * @param clientId The client ID
 * @param clientSecret The client secret
 * @param username Username (optional)
 * @param password Password (Required if username provided)
 */
export const authorize = (clientId: string, clientSecret: string, username?: string, password?: string): Thunk => async (dispatch, getState) => {
    const authResult = await new Api(getState()).authorize({
        clientId,
        clientSecret,
        username,
        password,
        grantType: "password",
    });

    Log.info("auth actions authorize result", authResult);

    if (authResult.isSuccess && authResult.data !== undefined) {
        authHelper.saveTokenToCookie(authResult.data.access_token, authResult.data.refresh_token);
        // reset tryouts
        authTryOuts = 0;
        // dispatch auth success
        dispatch(postAuthorize(authResult.data.access_token, authResult.data.refresh_token));
    }
    else {
        await dispatch(onAuthFailed(authResult));
        const isConnectionFail = Result.isConnectionFail(authResult);
        console.warn("connection fail?", isConnectionFail, authResult);
        // in case we get stuck trying to re-authorize (might be disconnected)
        authTryOuts++;

        if (authTryOuts < 10) {
            // await dispatch(revokeUserAccess(clientId, clientSecret));
        }
        else {
            console.warn("Are you disconnected?");
            setTimeout(() => {
                // dispatch(revokeUserAccess(clientId, clientSecret));
            }, 1000 * 30);
        }
    }
};

/**
 * Refreshes the access using a refresh token against the API.
 * @param clientId The client ID
 * @param clientSecret The client secret
 * @param refreshToken The refresh token
 */
export const refreshAccessToken = (clientId: string, clientSecret: string, refreshToken: string): Thunk => async (dispatch, getState) => {
    const authResult = await new Api(getState()).authorize({
        clientId,
        clientSecret,
        grantType: "refresh_token",
        refreshToken,
    });

    if (authResult.isSuccess && authResult.data !== undefined) {
        // dispatch auth success
        authHelper.saveTokenToCookie(authResult.data.access_token, authResult.data.refresh_token);
        return dispatch(postAuthorize(authResult.data.access_token, authResult.data.refresh_token));
    }
    else {
        await dispatch(onAuthFailed(authResult));

        console.info("auth actions refreshToken result failed", Result.isConnectionFail(authResult), authResult);
        if (!Result.isConnectionFail(authResult))
            return dispatch(revokeUserAccess(clientId, clientSecret));
    }
};

/**
 * Forces the authentication token to be refreshed
 */
export const forceAuthRefresh = (): Thunk => async (dispatch, getState) => {
    const state = getState();
    await dispatch(refreshAccessToken(state.auth.clientId, state.auth.clientSecret, state.auth.refreshToken || ""));
};

/**
 * Revokes api access, essentially logging out the client.
 */
export const revokeAccess = (): Thunk => async (dispatch, getState) => {
    AuthHelper.clearTokenFromCookie();
    await dispatch(onAccessRevoked());

    setTimeout(async () => {
        const state = getState();
        await dispatch(init(state.auth.clientId, state.auth.clientSecret));
    }, 125);
};

/**
 * Revokes api access for the user, logging out the client, and re-authorizing as anonymous client without user-access.
 */
export const revokeUserAccess = (clientId: string, clientSecret: string): Thunk => async (dispatch, getState) => {
    AuthHelper.clearTokenFromCookie();
    await dispatch(clearUserResources());
    await dispatch(onAccessRevoked());
    await dispatch(authorize(clientId, clientSecret));
};

/**
 * Revokes access, and redirects user to home page
 */
export const logOut = (): Thunk => async (dispatch, getState) => {
    await dispatch(onUserGuidChange(undefined));
    await dispatch(revokeAccess());
    await dispatch(push(Paths.Home));
};

export const postAuthorize = (accessToken: string, refreshToken: string): Thunk => async (dispatch, getState) => {
    const authState = getState().auth;
    const token = new AuthTokenDecoded(accessToken, refreshToken);
    if (token.isValid && token.tokenData && (!authState.data || (authState.data && authState.data.userGuid !== token.tokenData.userGuid))) {
        // dispatch user GUID changed
        dispatch(onUserGuidChange(token.tokenData.userGuid));
    }

    await dispatch(onAuthSuccess(accessToken, refreshToken));
    await dispatch(beginExpirationTimeout());
    await dispatch(beginLegacyAuthCheck());
    new Api(getState()).saveTokenToSession(accessToken);
};

/**
 * Clears any user-specific state data
 */
const clearUserResources = (): Thunk => async (dispatch, getState) => {
    await dispatch(CareerCenterActions.updateUserJobs(List()));
    await dispatch(CareerCenterActions.updateUserResumes(List()));

    await dispatch(AbstractActions.onEventsUpdated(List()));
    await dispatch(AbstractActions.onTypesUpdated(List()));
    await dispatch(AbstractActions.onUserAbstractsUpdated(List()));

    await dispatch(AbstractGradingActions.onAbstractsUpdated(List()));
    await dispatch(AbstractGradingActions.onListAbstractsUpdated(List()));
    await dispatch(AbstractGradingActions.onReviewerStatsUpdated(DefaultReviewerStats));

    await dispatch(AdminAbstractActions.onAbstractsUpdated(List()));
    await dispatch(AdminAbstractActions.onEventsUpdated(List()));
    await dispatch(AdminAbstractActions.onListAbstractsUpdated(List()));
    await dispatch(AdminAbstractActions.onTypesUpdated(List()));

    await dispatch(AdminEventActions.onEventsUpdated(List()));
    await dispatch(AdminEventActions.onListEventsUpdated(List()));
    await dispatch(AdminEventActions.onSubEventsUpdated(List()));
};

/**
 * Tries to use username and legacy auth cookie to log in user to legacy system
 * @param username Username
 * @param legacyCookie Legacy auth cookie
 */
const tryLegacyAuthorize = (username?: string, legacyCookie?: LegacyAuthCookie): Thunk => async (dispatch, getState) => {
    if (username && legacyAuthCookie) {
        const api = new LegacyApi();
        if (legacyAuthCookie.m) // legacy member login
            api.authorize(username, legacyAuthCookie.m, true).then();
        if (legacyAuthCookie.u) // legacy admin login
            api.authorize(username, legacyAuthCookie.u, true);
    }
};

export const onAccessRevoked = createAction(Constants.ON_ACCESS_REVOKED);

export const onAuthFailed = createAction<AuthFailedPayload, Result<any>>(
    Constants.ON_AUTH_FAILED, (result) => (result),
);

export const onAuthSuccess = createAction<AuthSuccessPayload, string, string>(
    Constants.ON_AUTH_SUCCESS, (accessToken, refreshToken) => ({ accessToken, refreshToken }),
);

export const onUserGuidChange = createAction<string | undefined, string | undefined>(
    Constants.USERGUID_CHANGED, (guid) => (guid),
);

export const clientCreds = createAction<AuthClientCredsPayload, string, string>(
    Constants.CLIENT_CREDS, (clientId, clientSecret) => ({
        clientId,
        clientSecret,
    }),
);

export const initialized = createAction(Constants.INITIALIZED);



// WEBPACK FOOTER //
// ./src/actions/AuthActions.ts